import { Button, Page, Tooltip } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { isQueryError, isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import type {
  CardType,
  ContentType,
  CustomCardIntegration,
  CustomCardRequestIntegration,
  HttpMethod,
} from 'lib-shared-types-api';
import {
  Path,
  SubmitHandler,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { integrationsApi } from 'apis/integrations';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../../../../../components/documentationButton/documentationButton';
import { cardV2 } from '../../../../cards.types';
import { OAuthFormData } from '../../../components/oAuth';
import { isResponseWithErrors } from '../../../utils/validateMessages';
import { CustomCardsForm } from '../../components/form/settingsIntegrationsCardsCustomForm';
import { getAuthorizationHeaderType } from '../../utils/getAuthorizationType/getAuthorizationType';

type CreateCustomCardFormValues = {
  authorizationHeader?: string;
  customHeaders?: Dictionary<string>;
  oAuthStrategy?: string;
} & Omit<CustomCardIntegration, 'id' | 'create_at' | 'request'> & {
    request: {
      http_method: HttpMethod[];
      content_type: ContentType[];
    } & Omit<CustomCardRequestIntegration, 'http_method' | 'content_type'>;
  };

/** Create custom cards page. */
export const SettingsIntegrationsCardsCustomCreateView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const [createCustomCard, { isLoading }] =
    integrationsApi.useCreateCustomCardIntegrationMutation();

  const {
    control: oAuthFormControl,
    formState: { isDirty: isOAuthFormDirty },
  } = useFormContext<OAuthFormData>();

  const [requestOAuthId] = useWatch({
    control: oAuthFormControl,
    name: [
      'request.o_auth_connection_id',
    ] as Path<CreateCustomCardFormValues>[],
  });

  const hasOAuthConnectionId = !!requestOAuthId;
  // We disable the custom card save button when we don't have a connection id
  // yet, but the OAuth form is dirty.
  const disableCustomCardSaveButton = !hasOAuthConnectionId && isOAuthFormDirty;

  const customCardForm = useForm<CreateCustomCardFormValues>({
    mode: 'onTouched',
    defaultValues: {
      request: {
        http_method: ['GET'],
        content_type: [],
        body: '',
      },
    },
  });

  const onSubmit: SubmitHandler<CreateCustomCardFormValues> = async (
    formValues,
  ) => {
    const uriTemplate = formValues?.request?.uri_template;
    const hasUriTemplate = !!uriTemplate && uriTemplate !== '';

    const basicFormValues = {
      title: formValues.title,
      template: formValues.template,
      card_version: cardV2,
      integration_type: 'custom' as CardType,
    };

    const authorizationHeader = formValues?.authorizationHeader;
    const customHeaders = formValues?.customHeaders;

    const requestFormValues = hasUriTemplate
      ? {
          request: {
            uri_template: uriTemplate,
            headers: {
              ...(!!customHeaders &&
                Object.keys(customHeaders).length > 0 && {
                  ...customHeaders,
                }),
              ...(!!authorizationHeader && {
                authorization: authorizationHeader,
              }),
            },
            body: !!formValues.request?.body
              ? formValues.request.body
              : undefined,
            http_method: formValues.request.http_method[0],
            content_type: formValues.request?.content_type?.[0] || undefined,
            o_auth_connection_id: formValues?.request?.o_auth_connection_id,
          } as CustomCardRequestIntegration,
        }
      : {};

    const response = await createCustomCard({
      ...basicFormValues,
      ...requestFormValues,
    });

    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Success',
          text: 'Custom card has been created.',
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      if (hasUriTemplate) {
        const authorizationHeaderType =
          getAuthorizationHeaderType(authorizationHeader) ||
          (formValues?.request?.o_auth_connection_id && 'oauth') ||
          undefined;

        ampli.settingsIntegrationsCustomCardCreated({
          customHeaders: customHeaders ? Object.keys(customHeaders).length : 0,
          authorizationHeader: authorizationHeaderType,
          oAuthStrategy: formValues?.oAuthStrategy,
          httpMethod: formValues.request.http_method[0],
          contentType: formValues?.request?.content_type?.[0] || undefined,
          body: !!formValues.request?.body,
        });
      } else {
        ampli.settingsIntegrationsCustomCardCreated();
      }

      const { data } = response;

      navigate({
        path: '/settings/integrations/custom-card/edit/:id',
        params: { path: { id: data.data.id } },
      });
    } else {
      const errorMessage =
        isQueryError(response.error) &&
        isResponseWithErrors(response.error.data)
          ? response.error.data.errors.join(' ')
          : null;

      notify(
        {
          title: 'Error while creating the custom card.',
          text: errorMessage,
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <Page.Section
      title="Create custom card"
      titleMeta={
        <SettingsIntegrationsDocumentationButton
          onClick={() => {
            ampli.settingsIntegrationsCardReadDocumentation({
              cardRoute: 'create',
              cardType: 'custom',
            });

            elevioClient.openArticle(articles.settings.cards.custom);
          }}
        />
      }
      controls={
        <Tooltip
          dark
          content={
            disableCustomCardSaveButton
              ? 'The OAuth configuration needs to be saved before the card is saved'
              : undefined
          }
        >
          <Button
            isLoading={isLoading}
            disabled={disableCustomCardSaveButton}
            onClick={customCardForm.handleSubmit(onSubmit)}
          >
            Save changes
          </Button>
        </Tooltip>
      }
    >
      <CustomCardsForm<CreateCustomCardFormValues>
        control={customCardForm.control}
        reset={customCardForm.reset}
        getValues={customCardForm.getValues}
        handleCustomCardSubmit={customCardForm.handleSubmit(onSubmit)}
      />
    </Page.Section>
  );
};
